import {
  Banknote, AlertCircle, Hammer, CheckCircle2, Car, Clock, Receipt, TrendingUp, Search,
  Calendar, Bell, FileText, Users, Package, ShieldCheck, Mail,
} from "lucide-react";
import { NumberPlate } from "@/components/NumberPlate";
import { StatusChip } from "@/components/StatusChip";

/* Faithful, static marketing previews built from the real Pro Garages UI.
   All data below is fictional demonstration data — no real customers, plates,
   invoices or financial figures. */

export function Frame({ label, children, className = "" }) {
  return (
    <div className={`overflow-hidden rounded-2xl border border-border bg-background shadow-xl shadow-midnight/10 ${className}`}>
      <div className="flex items-center gap-1.5 border-b border-border bg-secondary px-4 py-2.5">
        <span className="h-2.5 w-2.5 rounded-full bg-danger/60" />
        <span className="h-2.5 w-2.5 rounded-full bg-warning/70" />
        <span className="h-2.5 w-2.5 rounded-full bg-success/70" />
        <span className="ml-2 truncate text-[11px] font-semibold text-muted-foreground">{label}</span>
      </div>
      <div className="p-4">{children}</div>
    </div>
  );
}

const TONES = {
  success: "text-success bg-success/10",
  danger: "text-danger bg-danger-bg",
  warning: "text-warning bg-warning-bg",
  brand: "text-brand bg-brand-tertiary",
  midnight: "text-midnight bg-secondary",
};

function MiniStat({ icon: Icon, label, value, tone = "brand" }) {
  return (
    <div className="rounded-xl border border-border bg-card p-3 text-left">
      <div className={`mb-2 flex h-8 w-8 items-center justify-center rounded-lg ${TONES[tone]}`}><Icon className="h-4 w-4" /></div>
      <p className="font-display text-2xl font-bold leading-none text-midnight">{value}</p>
      <p className="mt-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">{label}</p>
    </div>
  );
}

export function DashboardPreview() {
  const jobs = [
    { vrm: "AB12 CDE", v: "Ford Transit", t: "Full service + MOT", s: "in_progress" },
    { vrm: "LT19 HGK", v: "VW Golf", t: "Front brake pads & discs", s: "awaiting_parts" },
    { vrm: "MA21 OXP", v: "BMW 320d", t: "Diagnostics — EML", s: "open" },
  ];
  return (
    <Frame label="Pro Garages · Dashboard">
      <div className="mb-3">
        <p className="font-display text-lg font-bold uppercase leading-none text-midnight">Welcome, Dave</p>
        <p className="text-[11px] text-muted-foreground">Your workshop at a glance</p>
      </div>
      <div className="grid grid-cols-4 gap-2">
        <MiniStat icon={Banknote} label="Revenue 7d" value="£3,240" tone="success" />
        <MiniStat icon={AlertCircle} label="Outstanding" value="£980" tone="danger" />
        <MiniStat icon={Hammer} label="Active jobs" value="7" tone="brand" />
        <MiniStat icon={CheckCircle2} label="Done today" value="4" tone="success" />
      </div>
      <p className="mb-2 mt-4 font-display text-sm font-bold uppercase text-midnight">Active jobs</p>
      <div className="divide-y divide-border overflow-hidden rounded-xl border border-border">
        {jobs.map((j) => (
          <div key={j.vrm} className="flex items-center gap-3 px-3 py-2.5">
            <NumberPlate vrm={j.vrm} size="sm" />
            <div className="min-w-0 flex-1">
              <p className="truncate text-xs font-bold text-midnight">{j.v}</p>
              <p className="truncate text-[11px] text-muted-foreground">{j.t}</p>
            </div>
            <StatusChip status={j.s} />
          </div>
        ))}
      </div>
    </Frame>
  );
}

export function CalendarPreview() {
  const days = ["Mon", "Tue", "Wed", "Thu", "Fri"];
  const blocks = {
    Mon: [{ t: "09:00", r: "MOT — AB12 CDE", tone: "bg-brand-tertiary text-brand-2" }],
    Tue: [{ t: "10:30", r: "Service — LT19 HGK", tone: "bg-success/10 text-success" }],
    Wed: [{ t: "08:30", r: "Brakes — MA21 OXP", tone: "bg-warning-bg text-warning" }, { t: "14:00", r: "Diagnostics", tone: "bg-brand-tertiary text-brand-2" }],
    Thu: [{ t: "11:00", r: "Tyres — RV68 ZZT", tone: "bg-success/10 text-success" }],
    Fri: [{ t: "09:30", r: "MOT — KP15 LMN", tone: "bg-brand-tertiary text-brand-2" }],
  };
  return (
    <Frame label="Pro Garages · Calendar">
      <div className="mb-2 flex items-center justify-between">
        <p className="font-display text-sm font-bold uppercase text-midnight">This week</p>
        <span className="rounded-full bg-midnight px-2.5 py-1 text-[10px] font-bold text-white">5 bookings</span>
      </div>
      <div className="grid grid-cols-5 gap-1.5">
        {days.map((d) => (
          <div key={d} className="rounded-lg border border-border bg-card p-1.5">
            <p className="mb-1.5 text-center text-[10px] font-bold uppercase text-muted-foreground">{d}</p>
            <div className="space-y-1">
              {(blocks[d] || []).map((b, i) => (
                <div key={i} className={`rounded-md px-1.5 py-1 ${b.tone}`}>
                  <p className="text-[9px] font-bold">{b.t}</p>
                  <p className="truncate text-[9px] leading-tight">{b.r}</p>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
      <div className="mt-3 flex items-center gap-2 rounded-lg border border-warning/40 bg-warning-bg px-3 py-2">
        <Bell className="h-3.5 w-3.5 text-warning" />
        <p className="text-[11px] font-semibold text-midnight">2 online booking requests awaiting your confirmation</p>
      </div>
    </Frame>
  );
}

export function MotPreview() {
  const history = [
    { d: "14 Mar 2026", r: "PASS", tone: "paid" },
    { d: "02 Mar 2025", r: "PASS", tone: "paid", note: "Advisory: nearside front tyre worn close to limit" },
    { d: "19 Feb 2024", r: "FAIL", tone: "overdue", note: "Headlamp aim · brake imbalance" },
  ];
  return (
    <Frame label="Pro Garages · DVLA / MOT Lookup">
      <div className="flex items-center gap-2 rounded-lg border border-border bg-card p-2">
        <Search className="h-4 w-4 text-muted-foreground" />
        <NumberPlate vrm="AB12 CDE" size="sm" />
        <button className="ml-auto rounded-md bg-brand px-3 py-1.5 text-[11px] font-bold text-white">Look up</button>
      </div>
      <div className="mt-3 rounded-xl border border-border bg-card p-3">
        <div className="flex items-center justify-between">
          <div>
            <p className="font-display text-sm font-bold text-midnight">Ford Transit 350</p>
            <p className="text-[11px] text-muted-foreground">2.0 EcoBlue · Diesel · 2018 · White</p>
          </div>
          <div className="text-right">
            <StatusChip status="paid" />
            <p className="mt-1 text-[10px] text-muted-foreground">MOT due 13 Mar 2027</p>
          </div>
        </div>
      </div>
      <p className="mb-1.5 mt-3 text-[11px] font-bold uppercase text-muted-foreground">MOT history</p>
      <div className="space-y-1.5">
        {history.map((h, i) => (
          <div key={i} className="rounded-lg border border-border bg-card px-3 py-2">
            <div className="flex items-center justify-between">
              <p className="text-[11px] font-bold text-midnight">{h.d}</p>
              <StatusChip status={h.tone} className="!text-[10px]" />
            </div>
            {h.note && <p className="mt-0.5 text-[10px] text-muted-foreground">{h.note}</p>}
          </div>
        ))}
      </div>
    </Frame>
  );
}

export function QuotePreview() {
  const lines = [
    { d: "Front brake pads (pair)", q: 1, p: "£58.00" },
    { d: "Front brake discs (pair)", q: 1, p: "£96.00" },
    { d: "Labour — 1.5 hrs", q: 1, p: "£67.50" },
  ];
  return (
    <Frame label="Pro Garages · Quotation">
      <div className="mb-3 flex items-center justify-between">
        <div>
          <p className="font-display text-sm font-bold uppercase text-midnight">Quote #Q-1043</p>
          <p className="text-[11px] text-muted-foreground">VW Golf · LT19 HGK</p>
        </div>
        <StatusChip status="pending" />
      </div>
      <div className="divide-y divide-border overflow-hidden rounded-xl border border-border">
        {lines.map((l) => (
          <div key={l.d} className="flex items-center justify-between px-3 py-2">
            <p className="text-[11px] text-midnight">{l.d}</p>
            <p className="text-[11px] font-bold text-midnight">{l.p}</p>
          </div>
        ))}
        <div className="flex items-center justify-between bg-secondary px-3 py-2">
          <p className="text-[11px] font-bold uppercase text-midnight">Total</p>
          <p className="font-display text-base font-bold text-midnight">£221.50</p>
        </div>
      </div>
      <div className="mt-3 flex items-center gap-2 rounded-lg border border-brand/30 bg-brand-tertiary px-3 py-2">
        <Mail className="h-3.5 w-3.5 text-brand-2" />
        <p className="text-[11px] font-semibold text-midnight">Emailed to customer — awaiting online approval</p>
      </div>
    </Frame>
  );
}

export function InvoicePreview() {
  return (
    <Frame label="Pro Garages · Invoice">
      <div className="mb-3 flex items-center justify-between">
        <div>
          <p className="font-display text-sm font-bold uppercase text-midnight">Invoice #INV-2087</p>
          <p className="text-[11px] text-muted-foreground">Ford Transit · AB12 CDE</p>
        </div>
        <StatusChip status="partial" />
      </div>
      <div className="grid grid-cols-3 gap-2">
        <div className="rounded-lg border border-border bg-card p-2.5 text-center">
          <p className="text-[9px] font-semibold uppercase text-muted-foreground">Total</p>
          <p className="font-display text-base font-bold text-midnight">£480.00</p>
        </div>
        <div className="rounded-lg border border-border bg-card p-2.5 text-center">
          <p className="text-[9px] font-semibold uppercase text-muted-foreground">Paid</p>
          <p className="font-display text-base font-bold text-success">£300.00</p>
        </div>
        <div className="rounded-lg border border-border bg-card p-2.5 text-center">
          <p className="text-[9px] font-semibold uppercase text-muted-foreground">Balance</p>
          <p className="font-display text-base font-bold text-danger">£180.00</p>
        </div>
      </div>
      <div className="mt-3 space-y-1.5">
        <div className="flex items-center justify-between rounded-lg border border-border bg-card px-3 py-2">
          <p className="text-[11px] text-midnight">Card payment · 12 Jun 2026</p>
          <p className="text-[11px] font-bold text-success">£200.00</p>
        </div>
        <div className="flex items-center justify-between rounded-lg border border-border bg-card px-3 py-2">
          <p className="text-[11px] text-midnight">Bank transfer · 10 Jun 2026</p>
          <p className="text-[11px] font-bold text-success">£100.00</p>
        </div>
      </div>
    </Frame>
  );
}

export function FinancePreview() {
  const bars = [40, 62, 48, 75, 58, 83];
  return (
    <Frame label="Pro Garages · Finance">
      <div className="mb-3 flex gap-1.5">
        {["7 days", "30 days", "Year", "All"].map((p, i) => (
          <span key={p} className={`rounded-full px-2.5 py-1 text-[10px] font-bold uppercase ${i === 1 ? "bg-midnight text-white" : "bg-secondary text-slate-500"}`}>{p}</span>
        ))}
      </div>
      <div className="grid grid-cols-4 gap-2">
        <MiniStat icon={Banknote} label="Revenue" value="£12.4k" tone="success" />
        <MiniStat icon={Clock} label="Outstanding" value="£1.9k" tone="warning" />
        <MiniStat icon={Receipt} label="Expenses" value="£4.8k" tone="midnight" />
        <MiniStat icon={TrendingUp} label="Net profit" value="£7.6k" tone="brand" />
      </div>
      <div className="mt-4 flex h-24 items-end gap-2 rounded-xl border border-border bg-card p-3">
        {bars.map((h, i) => (
          <div key={i} className="flex-1 rounded-t bg-brand/80" style={{ height: `${h}%` }} />
        ))}
      </div>
      <p className="mt-2 text-center text-[10px] text-muted-foreground">Monthly revenue · exportable to PDF / CSV</p>
    </Frame>
  );
}

export function CustomersPreview() {
  const rows = [
    { n: "D. Thompson", town: "Wolverhampton", v: "2 vehicles" },
    { n: "S. Kaur", town: "Bilston", v: "1 vehicle" },
    { n: "M. O'Brien", town: "Walsall", v: "3 vehicles" },
    { n: "A. Nowak", town: "Dudley", v: "1 vehicle" },
  ];
  return (
    <Frame label="Pro Garages · Customers">
      <p className="mb-2 font-display text-sm font-bold uppercase text-midnight">Customers &amp; vehicles</p>
      <div className="divide-y divide-border overflow-hidden rounded-xl border border-border">
        {rows.map((r) => (
          <div key={r.n} className="flex items-center gap-3 px-3 py-2.5">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-brand-tertiary text-[11px] font-bold text-brand-2">{r.n.split(" ").map((x) => x[0]).join("")}</div>
            <div className="min-w-0 flex-1">
              <p className="truncate text-xs font-bold text-midnight">{r.n}</p>
              <p className="truncate text-[11px] text-muted-foreground">{r.town}</p>
            </div>
            <span className="rounded-full bg-secondary px-2 py-0.5 text-[10px] font-bold text-slate-500">{r.v}</span>
          </div>
        ))}
      </div>
    </Frame>
  );
}

export function PartsPreview() {
  const rows = [
    { p: "Brake pads — front", s: "Euro Car Parts", q: 8, tone: "text-success" },
    { p: "Oil filter — 5W30", s: "GSF Car Parts", q: 3, tone: "text-warning" },
    { p: "Wiper blades 22\"", s: "Motor Factor Ltd", q: 0, tone: "text-danger" },
    { p: "Cabin filter", s: "Euro Car Parts", q: 12, tone: "text-success" },
  ];
  return (
    <Frame label="Pro Garages · Parts &amp; Suppliers">
      <p className="mb-2 font-display text-sm font-bold uppercase text-midnight">Inventory</p>
      <div className="overflow-hidden rounded-xl border border-border">
        <div className="grid grid-cols-[1fr_auto] gap-2 bg-secondary px-3 py-1.5 text-[10px] font-bold uppercase text-muted-foreground">
          <span>Part · supplier</span><span>In stock</span>
        </div>
        <div className="divide-y divide-border">
          {rows.map((r) => (
            <div key={r.p} className="grid grid-cols-[1fr_auto] items-center gap-2 px-3 py-2">
              <div className="min-w-0">
                <p className="truncate text-[11px] font-bold text-midnight">{r.p}</p>
                <p className="truncate text-[10px] text-muted-foreground">{r.s}</p>
              </div>
              <span className={`text-[11px] font-bold ${r.tone}`}>{r.q === 0 ? "Out" : r.q}</span>
            </div>
          ))}
        </div>
      </div>
    </Frame>
  );
}

export function TeamPreview() {
  const rows = [
    { n: "Dave Thompson", role: "Owner", tone: "bg-midnight text-white" },
    { n: "Jack Ryan", role: "Technician", tone: "bg-brand-tertiary text-brand-2" },
    { n: "Priya Shah", role: "Technician", tone: "bg-brand-tertiary text-brand-2" },
    { n: "Lee Carter", role: "Front desk", tone: "bg-secondary text-slate-600" },
  ];
  return (
    <Frame label="Pro Garages · Team">
      <p className="mb-2 font-display text-sm font-bold uppercase text-midnight">Team &amp; access</p>
      <div className="divide-y divide-border overflow-hidden rounded-xl border border-border">
        {rows.map((r) => (
          <div key={r.n} className="flex items-center gap-3 px-3 py-2.5">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-secondary text-[11px] font-bold text-slate-600">{r.n.split(" ").map((x) => x[0]).join("")}</div>
            <p className="min-w-0 flex-1 truncate text-xs font-bold text-midnight">{r.n}</p>
            <span className={`rounded-full px-2.5 py-0.5 text-[10px] font-bold ${r.tone}`}>{r.role}</span>
          </div>
        ))}
      </div>
    </Frame>
  );
}

export function NotificationsPreview() {
  const rows = [
    { icon: Calendar, t: "Booking confirmed", b: "MOT · AB12 CDE · Mon 09:00", tone: "text-success bg-success/10" },
    { icon: CheckCircle2, t: "Quote approved", b: "Quote #Q-1043 approved by customer", tone: "text-brand bg-brand-tertiary" },
    { icon: AlertCircle, t: "Invoice overdue", b: "Invoice #INV-2061 · £140 outstanding", tone: "text-danger bg-danger-bg" },
    { icon: Mail, t: "Receipt sent", b: "Payment receipt emailed to customer", tone: "text-midnight bg-secondary" },
  ];
  return (
    <Frame label="Pro Garages · Notifications">
      <p className="mb-2 font-display text-sm font-bold uppercase text-midnight">Notifications</p>
      <div className="divide-y divide-border overflow-hidden rounded-xl border border-border">
        {rows.map((r) => {
          const Icon = r.icon;
          return (
            <div key={r.t} className="flex items-center gap-3 px-3 py-2.5">
              <div className={`flex h-8 w-8 items-center justify-center rounded-lg ${r.tone}`}><Icon className="h-4 w-4" /></div>
              <div className="min-w-0 flex-1">
                <p className="truncate text-xs font-bold text-midnight">{r.t}</p>
                <p className="truncate text-[11px] text-muted-foreground">{r.b}</p>
              </div>
            </div>
          );
        })}
      </div>
    </Frame>
  );
}

export function JobPreview() {
  const steps = [
    { l: "Open", done: true },
    { l: "In progress", done: true, now: true },
    { l: "Awaiting parts", done: false },
    { l: "Completed", done: false },
    { l: "Invoiced", done: false },
  ];
  const items = [
    { d: "Oil & filter service", p: "£89.00" },
    { d: "Air filter replacement", p: "£24.00" },
    { d: "Brake fluid change", p: "£45.00" },
  ];
  return (
    <Frame label="Pro Garages · Job #J-3120">
      <div className="mb-3 flex items-center gap-3">
        <NumberPlate vrm="LT19 HGK" size="sm" />
        <div className="min-w-0 flex-1">
          <p className="truncate text-xs font-bold text-midnight">VW Golf 1.6 TDI</p>
          <p className="truncate text-[11px] text-muted-foreground">Service — assigned to Jack R.</p>
        </div>
        <StatusChip status="in_progress" />
      </div>
      <div className="mb-3 flex items-center">
        {steps.map((s, i) => (
          <div key={s.l} className="flex flex-1 flex-col items-center">
            <div className="flex w-full items-center">
              <span className={`h-1 flex-1 ${i === 0 ? "bg-transparent" : s.done ? "bg-brand" : "bg-border"}`} />
              <span className={`flex h-5 w-5 items-center justify-center rounded-full text-[9px] font-bold ${s.now ? "bg-brand text-white ring-2 ring-brand/30" : s.done ? "bg-brand text-white" : "bg-secondary text-slate-400"}`}>{i + 1}</span>
              <span className={`h-1 flex-1 ${i === steps.length - 1 ? "bg-transparent" : steps[i + 1].done ? "bg-brand" : "bg-border"}`} />
            </div>
            <span className={`mt-1 text-center text-[8px] font-semibold uppercase leading-tight ${s.done || s.now ? "text-midnight" : "text-slate-400"}`}>{s.l}</span>
          </div>
        ))}
      </div>
      <div className="divide-y divide-border overflow-hidden rounded-xl border border-border">
        {items.map((it) => (
          <div key={it.d} className="flex items-center justify-between px-3 py-2">
            <p className="text-[11px] text-midnight">{it.d}</p>
            <p className="text-[11px] font-bold text-midnight">{it.p}</p>
          </div>
        ))}
      </div>
    </Frame>
  );
}

export const MODULE_ICONS = { Car, Calendar, Hammer, Banknote, Users, Package, ShieldCheck, Bell, FileText, TrendingUp, Receipt, Search };
